import type { MeResponse, Tenant } from "@/lib/api";

export type Capability =
  | "analytics:read"
  | "reports:manage"
  | "fleet:read"
  | "controllers:read"
  | "controllers:configure"
  | "controllers:share"
  | "provisioning:write"
  | "users:manage"
  | "catalog:write";

// Never infer permissions from roles or from other organization memberships.
export function hasCapability(me: MeResponse | null, capability: Capability): boolean {
  return me?.capabilities?.includes(capability) ?? false;
}

export function provisioningVendors(me: MeResponse | null, tenants: Tenant[]): Tenant[] {
  if (!hasCapability(me, "provisioning:write")) return [];
  if (hasCapability(me, "catalog:write")) return tenants.filter((tenant) => tenant.kind === "vendor");
  if (me?.active_organization_kind !== "vendor" || !me.tenant_id) return [];
  return [{
    tenant_id: me.tenant_id,
    name: me.organizations.find((org) => org.tenant_id === me.tenant_id)?.name ?? `Vendor #${me.tenant_id}`,
    external_key: "",
    kind: "vendor",
  }];
}

// Recheck at submission: stale or manipulated selector state cannot override
// the active vendor context.
export function provisioningTenantId(me: MeResponse | null, tenants: Tenant[], selected: string): number {
  const vendors = provisioningVendors(me, tenants);
  const tenantId = hasCapability(me, "catalog:write") ? Number(selected) : me?.tenant_id;
  if (!tenantId || !vendors.some((vendor) => vendor.tenant_id === tenantId)) {
    throw new Error("Choose an authorized vendor");
  }
  return tenantId;
}
